import { PropType, defineComponent, h, ref, watch } from 'vue';
import { Dialog, utility } from '@phi/components';
import { FormText } from '@components/form';
import IconExplanationFormSelect from '../IconExplanationFormSelect';
import { save_model } from '../../input_output';
import { reject_symbols } from '../../keyboard';
import { IFrontendServiceContainer } from '@phi/frontend/service-container';
import { set } from '@phi/util/vuetils';

const save_to_onshape_icon = `<svg viewBox="0 0 64 64" class="save-icon">
    <linearGradient id="a" x1="50%" x2="50%" y1="9.21%" y2="85.26%">
        <stop offset="0%" />
        <stop offset="100%" stop-color="#999" />
    </linearGradient>
    <linearGradient id="b" x1="50%" x2="50%" y1="9.21%" y2="85.27%">
        <stop offset="0%" stop-color="#999" />
        <stop offset="100%" />
    </linearGradient>
    <use xlink:href="#onshape"></use>
</svg>`;

const save_locally_icon = `<svg viewBox="0 0 64 64" class="save-icon">
    <linearGradient id="a" x1="50%" x2="50%" y1="9.21%" y2="85.26%">
        <stop offset="0%" />
        <stop offset="100%" stop-color="#999" />
    </linearGradient>
    <linearGradient id="b" x1="50%" x2="50%" y1="9.21%" y2="85.27%">
        <stop offset="0%" stop-color="#999" />
        <stop offset="100%" />
    </linearGradient>
    <use xlink:href="#phifile"></use>
</svg>`;

export default defineComponent({
    props: {
        app: { required: true, type: Object as PropType<IFrontendServiceContainer> },
    },

    setup({ app }) {
        // types
        type SaveMethod = 'onshape' | 'local';

        // components
        const SelectSaveMethod = IconExplanationFormSelect<SaveMethod>();

        // computed
        const can_save = () =>
            save_method.value !== undefined
            && filename.value.length > 0;

        const initial_save_method = (): SaveMethod =>
            app.authentication.session_type.value.has('onshape')
                ? 'onshape'
                : 'local';

        // data
        const filename = ref(app.state.current_filename);
        const save_method = ref(initial_save_method());

        // methods
        function reset() {
            filename.value = app.state.current_filename;
            save_method.value = initial_save_method();
        }

        function on_cancel() {
            app.state.modal.savePhiModelModal = false;
        }

        function on_submit() {
            app.state.modal.savePhiModelModal = false;
            if (can_save())
                save_model(filename.value!, save_method.value!);
        }

        // watchers
        watch(
            () => app.state.modal.savePhiModelModal,
            (value) => {
                if (value)
                    reset();
            },
        );

        // render
        return () => h(Dialog, {
            show: app.state.modal.savePhiModelModal,
            'onUpdate:show': x => app.state.modal.savePhiModelModal = x,
            onClose: on_cancel,
            confirm_label: 'Save',
            confirm_enabled: can_save(),
            onConfirm: on_submit,
            id: 'savePhiModelModal',
            title: 'Save Phi model',
        }, () => h('div', { class: utility.margin_gap_5 }, [
            h('section', [
                h('label', { for: 'save-phi-model-file-name' }, 'Model name'),

                h(FormText, {
                    value: filename.value,
                    'onUpdate:value': set(filename),
                    id: 'save-phi-model-file-name',
                    onKeydown: reject_symbols,
                }),
            ]),
            h('section', [
                h('p', 'Save method'),

                h(SelectSaveMethod, {
                    value: save_method.value,
                    'onUpdate:value': set(save_method),
                    options: [
                        {
                            value: 'local',
                            icon: h('div', { class: 'icon', innerHTML: save_locally_icon }),
                            title: 'Save Phi model to file',
                            subtitle: 'Your Phi model will be saved to your computer as a file in the .phi format.',
                            disabled: false,
                        },

                        {
                            value: 'onshape',
                            icon: h('div', { innerHTML: save_to_onshape_icon }),
                            title: 'Save Phi model in Onshape',
                            subtitle: 'Your Phi model will be stored in your Onshape document in a new tab in the .phi format.',
                            disabled: !app.authentication.session_type.value.has('onshape'),
                        },
                    ],
                }),
            ]),
        ]));
    },
});
